import React, { useEffect, useState } from "react";
import {
  Box,
  Modal,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { Icons } from "../../doctor-console/Utils/iconsRegistry";
import axios from "axios";
import { toast } from "react-toastify";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const DoctorScheduleModal = ({ open, handleClose, doctorId }) => {
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const [invalidShifts, setInvalidShifts] = useState([]);
  const [schedule, setSchedule] = useState(
    daysOfWeek.map((day) => ({
      day,
      shifts: [
        { start: null, end: null, patientCount: 0 },
        { start: null, end: null, patientCount: 0 },
        { start: null, end: null, patientCount: 0 },
      ],
    }))
  );
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  console.log("schedule", schedule);

  useEffect(() => {
    if (open && doctorId) {
      fetchDoctorSchedule(doctorId);
    }
  }, [open, doctorId]);

  const closeHandler = () => {
    setSchedule(
      daysOfWeek.map((day) => ({
        day,
        shifts: [
          { start: null, end: null, patientCount: 0 },
          { start: null, end: null, patientCount: 0 },
          { start: null, end: null, patientCount: 0 },
        ],
      }))
    );
    handleClose();
  };

  const fetchDoctorSchedule = async (doctorId) => {
    try {
      const res = await axios.get(
        `${REACT_APP_BACKEND_ROUTE_URL}doctor_schedules/${doctorId}/`,
        {
          headers: headers,
        }
      );
      // Validate API-level success and schema flag
      if (!res?.data || res.data.status !== "Success") {
        console.warn("Doctor schedule API did not return Success:", res?.data);
        return; // keep default schedule
      }

      if (res.data?.data?.length) {
        const apiData = res.data.data;

        // Map API data to your internal `schedule` structure
        const updatedSchedule = daysOfWeek.map((day) => {
          const apiDay = apiData.find(
            (d) => d.shift_day.toLowerCase() === day.toLowerCase()
          );

          if (apiDay) {
            return {
              day,
              shifts: [
                {
                  start: apiDay.first_shift
                    ? dayjs(apiDay.first_shift_start_time, "HH:mm:ss")
                    : null,
                  end: apiDay.first_shift
                    ? dayjs(apiDay.first_shift_end_time, "HH:mm:ss")
                    : null,
                  patientCount: apiDay.first_shift_patient_visit_count || 0,
                },
                {
                  start: apiDay.second_shift
                    ? dayjs(apiDay.second_shift_start_time, "HH:mm:ss")
                    : null,
                  end: apiDay.second_shift
                    ? dayjs(apiDay.second_shift_end_time, "HH:mm:ss")
                    : null,
                  patientCount: apiDay.second_shift_patient_visit_count || 0,
                },
                {
                  start: apiDay.third_shift
                    ? dayjs(apiDay.third_shift_start_time, "HH:mm:ss")
                    : null,
                  end: apiDay.third_shift
                    ? dayjs(apiDay.third_shift_end_time, "HH:mm:ss")
                    : null,
                  patientCount: apiDay.third_shift_patient_visit_count || 0,
                },
              ],
            };
          } else {
            // Keep default if not found in API
            return {
              day,
              shifts: [
                { start: null, end: null, patientCount: 0 },
                { start: null, end: null, patientCount: 0 },
                { start: null, end: null, patientCount: 0 },
              ],
            };
          }
        });

        setSchedule(updatedSchedule);
      }
    } catch (error) {
      console.error("Error fetching doctor schedule:", error);
    }
  };

  const handleTimeChange = (dayIndex, shiftIndex, field, value) => {
    const updated = [...schedule];
    updated[dayIndex].shifts[shiftIndex][field] = value;
    setSchedule(updated);
  };

  function adjustTimeToLocal(inputTime) {
    // Convert the input UTC time string to a Date object
    const date = new Date(inputTime);

    // Convert the UTC time to the local time zone
    const localTime = new Date(
      date.toLocaleString("en-US", {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    );

    // Format the time as HH:mm:ss (local time)
    const hours = String(localTime.getHours()).padStart(2, "0");
    const minutes = String(localTime.getMinutes()).padStart(2, "0");
    const seconds = String(localTime.getSeconds()).padStart(2, "0");

    // Return the formatted time
    return `${hours}:${minutes}:${seconds}`;
  }

  const prepareDoctorSchedulePayload = (doctorId, schedule) => {
    return schedule.map((dayItem) => ({
      // doctor_id: doctorId,
      shift_day: dayItem.day.toLowerCase(),
      first_shift: !!(dayItem.shifts[0].start && dayItem.shifts[0].end),
      first_shift_start_time: !!(
        dayItem.shifts[0].start && dayItem.shifts[0].end
      )
        ? adjustTimeToLocal(dayItem.shifts[0].start)
        : null,
      first_shift_end_time: !!(dayItem.shifts[0].start && dayItem.shifts[0].end)
        ? adjustTimeToLocal(dayItem.shifts[0].end)
        : null,
      first_shift_patient_visit_count: dayItem.shifts[0].patientCount || 0,

      second_shift: !!(dayItem.shifts[1].start && dayItem.shifts[1].end),
      second_shift_start_time: !!(
        dayItem.shifts[1].start && dayItem.shifts[1].end
      )
        ? adjustTimeToLocal(dayItem.shifts[1].start)
        : null,
      second_shift_end_time: !!(
        dayItem.shifts[1].start && dayItem.shifts[1].end
      )
        ? adjustTimeToLocal(dayItem.shifts[1].end)
        : null,
      second_shift_patient_visit_count: dayItem.shifts[1].patientCount || 0,

      third_shift: !!(dayItem.shifts[2].start && dayItem.shifts[2].end),
      third_shift_start_time: !!(
        dayItem.shifts[2].start && dayItem.shifts[2].end
      )
        ? adjustTimeToLocal(dayItem.shifts[2].start)
        : null,
      third_shift_end_time: !!(dayItem.shifts[2].start && dayItem.shifts[2].end)
        ? adjustTimeToLocal(dayItem.shifts[2].end)
        : null,
      third_shift_patient_visit_count: dayItem.shifts[2].patientCount || 0,
    }));
  };

  const validateSchedule = (schedule) => {
    const invalidShifts = [];

    for (let dayIndex = 0; dayIndex < schedule.length; dayIndex++) {
      const dayData = schedule[dayIndex];
      const shifts = dayData.shifts;

      // --- Single-shift validations ---
      for (let shiftIndex = 0; shiftIndex < shifts.length; shiftIndex++) {
        const shift = shifts[shiftIndex];

        // Rule 1: If patientCount > 0 => start & end required
        if (shift.patientCount > 0) {
          if (!shift.start || !shift.end) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex,
              errorMessage: `Shift ${
                shiftIndex + 1
              }: Start time and End time must be filled`,
            });
          }

          // Rule 2: End time cannot be before start
          if (
            shift.start &&
            shift.end &&
            dayjs(shift.end).isBefore(shift.start)
          ) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex,
              errorMessage: `Shift ${
                shiftIndex + 1
              }: Start time cannot be after End time`,
            });
          }

          // Rule 3: Start and End time cannot be same
          if (
            shift.start &&
            shift.end &&
            dayjs(shift.start).isSame(shift.end)
          ) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex,
              errorMessage: `Shift ${
                shiftIndex + 1
              }: Start time and End time cannot be the same`,
            });
          }
        }

        // Rule 4: If start/end present => patientCount must be > 0
        if ((shift.start || shift.end) && shift.patientCount <= 0) {
          invalidShifts.push({
            day: dayData.day,
            shiftIndex,
            errorMessage: `Shift ${
              shiftIndex + 1
            }: Patient count must be greater than 0 if a shift time is provided`,
          });
        }
      }

      // --- Multi-shift validations (compare all pairs) ---
      for (let i = 0; i < shifts.length; i++) {
        const shiftA = shifts[i];
        if (!shiftA.start || !shiftA.end) continue;

        const startA = dayjs(shiftA.start);
        const endA = dayjs(shiftA.end);

        for (let j = i + 1; j < shifts.length; j++) {
          const shiftB = shifts[j];
          if (!shiftB.start || !shiftB.end) continue;

          const startB = dayjs(shiftB.start);
          const endB = dayjs(shiftB.end);

          // Rule 5a: Identical start or end times between any two shifts
          if (
            startA.isSame(startB) ||
            endA.isSame(endB) ||
            (startA.isSame(startB) && endA.isSame(endB))
          ) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex: j,
              errorMessage: `Shift ${i + 1} and Shift ${
                j + 1
              }: cannot have identical start or end times`,
            });
          }

          // Rule 5b: Overlapping or unordered shifts (regardless of order)
          // If shiftB starts before shiftA ends → overlap
          if (startB.isBefore(endA) && endB.isAfter(startA)) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex: j,
              errorMessage: `Shift ${i + 1} overlaps with Shift ${j + 1}`,
            });
          }

          // Rule 5c: If one shift fully before another but out of order
          if (endB.isBefore(startA)) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex: j,
              errorMessage: `Shift ${j + 1} occurs before Shift ${
                i + 1
              } — shifts must be in chronological order`,
            });
          }
        }
      }
    }

    return { isValid: invalidShifts.length === 0, invalidShifts };
  };

  const onSave = async () => {
    // Check if all shifts have null or empty values (start, end, patientCount)
    const isScheduleEmpty = schedule.every((dayData) =>
      dayData.shifts.every(
        (shift) =>
          !shift.start &&
          !shift.end &&
          (shift.patientCount === 0 || shift.patientCount === "")
      )
    );

    if (isScheduleEmpty) {
      toast.error("You need to fill the schedule before saving.");
      return; // Prevent saving if schedule is empty
    }
    const { isValid, invalidShifts } = validateSchedule(schedule);

    if (!isValid) {
      // Update the state with invalid shifts for UI rendering
      setInvalidShifts(invalidShifts);

      // Display the error toast with the first invalid shift's details
      toast.error(
        `${invalidShifts[0].errorMessage} on ${invalidShifts[0].day}`
        //  - Shift ${
        //   invalidShifts[0].shiftIndex + 1
        // }`
      );
      return; // Prevent saving if validation fails
    }

    const payload = prepareDoctorSchedulePayload(doctorId, schedule);

    try {
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}doctor_schedules/${doctorId}/`,
        payload,
        {
          headers: headers,
        }
      );

      if (!res?.data || res?.data?.status !== "Success") {
        toast.error(res?.data?.message || "Schedule Updation Unsuccessful!");
      } else {
        toast.success("Schedule Updated Successfully!");
      }
    } catch (error) {
      console.error("Error updating doctor schedule:", error);
    }
  };

  const handleResetShift = (dayIndex, shiftIndex) => {
    setSchedule((prev) => {
      const updated = [...prev];
      updated[dayIndex] = {
        ...updated[dayIndex],
        shifts: updated[dayIndex].shifts.map((shift, i) =>
          i === shiftIndex
            ? { start: null, end: null, patientCount: 0 } // reset only this shift
            : shift
        ),
      };
      return updated;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={closeHandler}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: "90%", // responsive width (mobile-friendly)
          maxWidth: "1500px", // never exceed 1500px
          maxHeight: "90vh",
          zIndex: 9997,
        },
      }}
    >
      <DialogTitle
        sx={{
          // backgroundImage: `url(${Icons.modal_blue})`,
          backgroundColor: "#213360",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          className="main-title"
          style={{
            textAlign: "center",
            color: "#FFFFFF",
            margin: "0px",
            fontSize: "20px",
          }}
        >
          Set Standard Schedule
        </div>
        <IconButton onClick={closeHandler}>
          <CloseIcon
            sx={{
              color: "#FFFFFF",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ maxHeight: "calc(80vh - 100px)", overflowY: "auto" }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <Box
            sx={{
              width: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {/* Header Row */}
            <Box
              display="grid"
              gridTemplateColumns="
        130px
        repeat(3, minmax(90px, 120px)) 15px 15px
        repeat(3, minmax(90px, 120px)) 15px 15px
        repeat(3, minmax(90px, 120px)) 15px 15px
      "
              alignItems="center"
              sx={{
                fontWeight: 600,
                fontSize: "0.9rem",
                color: "#444",
                mb: 0.5,
                columnGap: "10px",
                borderBottom: "2px solid #ccc",
                width: "fit-content",
              }}
            >
              <Box
                sx={{ ml: 1.5, borderRight: "2px solid #ccc", height: "100%" }}
              >
                Day
              </Box>
              <Box textAlign="center" gridColumn="span 3">
                1st Shift
              </Box>
              <Box />{" "}
              <Box sx={{ borderRight: "2px solid #ccc", height: "100%" }} />{" "}
              {/* divider */}
              <Box textAlign="center" gridColumn="span 3">
                2nd Shift
              </Box>
              <Box />{" "}
              <Box sx={{ borderRight: "2px solid #ccc", height: "100%" }} />{" "}
              {/* divider */}
              <Box textAlign="center" gridColumn="span 3">
                3rd Shift
              </Box>
              <Box />{" "}
            </Box>

            {/* Sub Header Row */}
            <Box
              display="grid"
              gridTemplateColumns="
        130px
        repeat(3, minmax(90px, 120px)) 15px 15px
        repeat(3, minmax(90px, 120px)) 15px 15px
        repeat(3, minmax(90px, 120px)) 15px 15px
      "
              alignItems="center"
              sx={{
                fontWeight: 500,
                fontSize: "0.8rem",
                color: "#666",
                columnGap: "10px",
                borderBottom: "1px solid #ccc",
                mb: 0.5,
                width: "fit-content",
              }}
            >
              <Box />
              <Box textAlign="center">Start Time</Box>
              <Box textAlign="center">End Time</Box>
              <Box textAlign="center">Patient Count</Box>
              <Box />{" "}
              <Box
                sx={{ borderRight: "2px solid #ccc", height: "100%" }}
              />{" "}
              {/* divider */}
              <Box textAlign="center">Start Time</Box>
              <Box textAlign="center">End Time</Box>
              <Box textAlign="center">Patient Count</Box>
              <Box />{" "}
              <Box
                sx={{ borderRight: "2px solid #ccc", height: "100%" }}
              />{" "}
              {/* divider */}
              <Box textAlign="center">Start Time</Box>
              <Box textAlign="center">End Time</Box>
              <Box textAlign="center">Patient Count</Box>
              <Box />{" "}
            </Box>

            {/* Schedule Rows */}
            {schedule.map((dayData, dayIndex) => (
              <Box
                key={dayData.day}
                display="grid"
                gridTemplateColumns="
              130px
              repeat(3, minmax(90px, 120px)) 15px 15px
              repeat(3, minmax(90px, 120px)) 15px 15px
              repeat(3, minmax(90px, 120px)) 15px 15px
            "
                alignItems="center"
                sx={{
                  width: "fit-content",
                  height: "auto",
                  p: 0.5,
                  borderRadius: 1,
                  mb: 0.8,
                  bgcolor: dayIndex % 2 === 0 ? "#fafafa" : "#EBFAF9",
                  border: "1px solid #e0e0e0",
                  columnGap: "10px",
                }}
              >
                <Box
                  fontWeight={500}
                  sx={{
                    borderRight: "2px solid #ccc",
                    pr: 1,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  {dayData.day}
                </Box>

                {dayData.shifts.map((shift, shiftIndex) => {
                  const invalidShift = invalidShifts.find(
                    (invalidShift) =>
                      invalidShift.day === dayData.day &&
                      invalidShift.shiftIndex === shiftIndex
                  );

                  return (
                    <Box key={shiftIndex} sx={{ display: "contents" }}>
                      <TimePicker
                        ampm
                        format="hh:mm A"
                        value={shift.start}
                        desktopModeMediaQuery="@media (pointer: none), (pointer: coarse)" // mobile behavior on all devices
                        onChange={(val) =>
                          handleTimeChange(dayIndex, shiftIndex, "start", val)
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            InputProps: {
                              endAdornment: (
                                <AccessTimeIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "action.active",
                                    position: "absolute",
                                    right: 4,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none", // decorative only
                                  }}
                                />
                              ),
                            },
                            inputProps: {
                              placeholder: "HH:MM",
                              readOnly: true,
                            },
                            sx: {
                              minWidth: 90,
                              position: "relative",
                              cursor: "pointer", //pointer cursor for entire field
                              "& .MuiInputBase-input": {
                                textTransform: "uppercase",
                                fontSize: "0.8rem",
                                padding: "6px 4px",
                                cursor: "pointer", //ensures text area also shows pointer
                              },
                              "& .MuiInputBase-root": {
                                cursor: "pointer", //ensures outer wrapper also shows pointer
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 16,
                              },
                            },
                            error: invalidShift && invalidShift.errorMessage,
                          },
                        }}
                      />

                      <TimePicker
                        ampm
                        format="hh:mm A"
                        value={shift.end}
                        desktopModeMediaQuery="@media (pointer: none), (pointer: coarse)" // mobile behavior on all devices
                        onChange={(val) =>
                          handleTimeChange(dayIndex, shiftIndex, "end", val)
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            InputProps: {
                              endAdornment: (
                                <AccessTimeIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "action.active",
                                    position: "absolute",
                                    right: 4,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none", // decorative only
                                  }}
                                />
                              ),
                            },
                            inputProps: {
                              placeholder: "HH:MM",
                              readOnly: true,
                            },
                            sx: {
                              minWidth: 90,
                              position: "relative",
                              cursor: "pointer", //pointer cursor for entire field
                              "& .MuiInputBase-input": {
                                textTransform: "uppercase",
                                fontSize: "0.8rem",
                                padding: "6px 4px",
                                cursor: "pointer", //ensures text area also shows pointer
                              },
                              "& .MuiInputBase-root": {
                                cursor: "pointer", //ensures outer wrapper also shows pointer
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 16,
                              },
                            },
                            error: invalidShift && invalidShift.errorMessage,
                          },
                        }}
                      />

                      <TextField
                        type="number"
                        size="small"
                        placeholder="0"
                        value={shift.patientCount || ""}
                        onChange={(e) =>
                          handleTimeChange(
                            dayIndex,
                            shiftIndex,
                            "patientCount",
                            e.target.value
                          )
                        }
                        inputProps={{
                          min: 0,
                          style: {
                            textAlign: "center",
                            fontSize: "0.8rem",
                            padding: "6px 4px",
                          },
                        }}
                        sx={{
                          minWidth: 70,
                          "& .MuiInputBase-input": {
                            textAlign: "center",
                          },
                        }}
                        error={invalidShift && invalidShift.errorMessage}
                      />

                      {/* Reset Icon */}
                      <Tooltip title="Reset">
                        <IconButton
                          size="small"
                          onClick={() => handleResetShift(dayIndex, shiftIndex)}
                          // disabled={isDisabled}
                          sx={{
                            color: "#666",
                            "&:hover": { color: "#000" },
                            "&.Mui-disabled": { color: "#bbb" },
                          }}
                        >
                          <RestartAltIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {/* Divider after each shift block */}
                      {/* {shiftIndex !== dayData.shifts.length - 1 && ( */}
                      <Box
                        sx={{
                          borderRight: "2px solid #ddd",
                          height: "100%",
                        }}
                      />
                      {/* )} */}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </LocalizationProvider>

        {/* </Box> */}
      </DialogContent>
      <DialogActions>
        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          {/* Buttons */}

          <Button
            variant="contained"
            sx={{
              borderRadius: "10px",
              backgroundColor: "#121959",
              color: "#fff",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              "&:hover": {
                backgroundColor: "#121959",
              },
            }}
            onClick={closeHandler}
            size="small"
          >
            Close
          </Button>

          <Button
            variant="contained"
            sx={{
              borderRadius: "10px",
              backgroundColor: "#121959",
              color: "#fff",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              "&:hover": {
                backgroundColor: "#121959",
              },
            }}
            onClick={onSave}
            size="small"
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DoctorScheduleModal;
